import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalysis } from '../context/AnalysisContext';
import Header from '../components/Header';
import LogService, { LOG_ACTIONS } from '../services/LogService';

const DEFAULT_SPECS = {
  peDireito: 2.80,
  tipoConstrucao: 'Residencial',
  faseObra: 'Construção nova',
  tipoParede: 'Alvenaria bloco cerâmico',
  espessuraRebocoInterno: 2.0,
  espessuraRebocoExterno: 3.0,
  pinturaInterna: 'Látex PVA',
  pinturaExterna: 'Acrílica',
  numeroDemaos: '2',
  tipoPisoPadrao: 'Cerâmica',
  tipoPisoMolhado: 'Cerâmica',
  incluirArgamassa: true,
  incluirRejunte: true,
  tipoRodape: 'Cerâmica',
  alturaRodape: 10,
  alturaRevBanheiro: 150,
  alturaRevCozinha: 60,
  alturaRevAreaServico: 120,
  tipoForro: 'Laje (pintura direto)',
  pinturaTeto: true,
  materialPortas: 'Madeira',
  materialJanelas: 'Alumínio',
  vasoSanitario: 'Com caixa acoplada',
  cubaBanheiro: 'De apoio',
  cubaCozinha: 'Inox simples',
  tanque: 'Tanque de louça',
  tipoTorneiras: 'Cromada simples',
  tipoChuveiro: 'Chuveiro elétrico',
  tipoFundacao: 'Sapata',
  fckConcreto: '25 MPa',
  tipoLaje: 'Pré-moldada',
};

const SECTIONS = [
  {
    id: 'geral',
    title: 'Informações Gerais',
    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
    fields: [
      { key: 'peDireito', label: 'Pé-direito padrão', type: 'number', suffix: 'm', step: 0.01 },
      { key: 'tipoConstrucao', label: 'Tipo de construção', type: 'select', options: ['Residencial', 'Comercial', 'Industrial'] },
      { key: 'faseObra', label: 'Fase da obra', type: 'select', options: ['Construção nova', 'Reforma'] },
    ],
  },
  {
    id: 'paredes',
    title: 'Paredes',
    icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    fields: [
      { key: 'tipoParede', label: 'Tipo de parede', type: 'select', options: ['Alvenaria bloco cerâmico', 'Alvenaria bloco concreto', 'Drywall'] },
      { key: 'espessuraRebocoInterno', label: 'Espessura reboco interno', type: 'number', suffix: 'cm', step: 0.5 },
      { key: 'espessuraRebocoExterno', label: 'Espessura reboco externo', type: 'number', suffix: 'cm', step: 0.5 },
    ],
  },
  {
    id: 'pintura',
    title: 'Pintura',
    icon: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01',
    fields: [
      { key: 'pinturaInterna', label: 'Pintura interna', type: 'select', options: ['Látex PVA', 'Acrílica', 'Sem pintura'] },
      { key: 'pinturaExterna', label: 'Pintura externa', type: 'select', options: ['Acrílica', 'Textura', 'Sem pintura'] },
      { key: 'numeroDemaos', label: 'Número de demãos', type: 'select', options: ['2', '3'] },
    ],
  },
  {
    id: 'piso',
    title: 'Piso',
    icon: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z',
    fields: [
      { key: 'tipoPisoPadrao', label: 'Tipo de piso padrão', type: 'select', options: ['Cerâmica', 'Porcelanato', 'Laminado', 'Vinílico', 'Cimento queimado'] },
      { key: 'tipoPisoMolhado', label: 'Tipo de piso áreas molhadas', type: 'select', options: ['Cerâmica', 'Porcelanato'] },
      { key: 'incluirArgamassa', label: 'Incluir argamassa colante', type: 'checkbox' },
      { key: 'incluirRejunte', label: 'Incluir rejunte', type: 'checkbox' },
    ],
  },
  {
    id: 'rodape',
    title: 'Rodapé',
    icon: 'M4 6h16M4 10h16M4 14h16M4 18h16',
    fields: [
      { key: 'tipoRodape', label: 'Tipo de rodapé', type: 'select', options: ['Cerâmica', 'Porcelanato', 'Madeira', 'Poliestireno', 'Sem rodapé'] },
      { key: 'alturaRodape', label: 'Altura do rodapé', type: 'number', suffix: 'cm', step: 1 },
    ],
  },
  {
    id: 'molhadas',
    title: 'Revestimento Áreas Molhadas',
    icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
    fields: [
      { key: 'alturaRevBanheiro', label: 'Altura revestimento banheiro', type: 'number', suffix: 'cm', step: 10 },
      { key: 'alturaRevCozinha', label: 'Altura revestimento cozinha', type: 'number', suffix: 'cm', step: 10 },
      { key: 'alturaRevAreaServico', label: 'Altura revestimento área serviço', type: 'number', suffix: 'cm', step: 10 },
    ],
  },
  {
    id: 'forro',
    title: 'Forro / Teto',
    icon: 'M3 4h18M3 4v16h18V4M3 4l9 6 9-6',
    fields: [
      { key: 'tipoForro', label: 'Tipo de forro', type: 'select', options: ['Laje (pintura direto)', 'Gesso', 'PVC', 'Sem forro'] },
      { key: 'pinturaTeto', label: 'Pintura no teto', type: 'checkbox' },
    ],
  },
  {
    id: 'esquadrias',
    title: 'Esquadrias',
    icon: 'M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M10 21V10M3 7l9-4 9 4',
    fields: [
      { key: 'materialPortas', label: 'Material portas internas', type: 'select', options: ['Madeira', 'MDF', 'Alumínio'] },
      { key: 'materialJanelas', label: 'Material janelas', type: 'select', options: ['Alumínio', 'PVC', 'Madeira', 'Vidro temperado'] },
    ],
  },
  {
    id: 'loucas',
    title: 'Louças e Metais',
    icon: 'M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728M12 9v3m0 0v3m0-3h3m-3 0H9',
    fields: [
      { key: 'vasoSanitario', label: 'Vasos sanitários', type: 'select', options: ['Com caixa acoplada', 'Com válvula', 'Suspenso'] },
      { key: 'cubaBanheiro', label: 'Cubas banheiro', type: 'select', options: ['De apoio', 'De embutir', 'Coluna'] },
      { key: 'cubaCozinha', label: 'Cubas cozinha', type: 'select', options: ['Inox simples', 'Inox dupla', 'Granito'] },
      { key: 'tanque', label: 'Tanque', type: 'select', options: ['Tanque de louça', 'Tanque inox', 'Sem tanque'] },
      { key: 'tipoTorneiras', label: 'Tipo de torneiras', type: 'select', options: ['Cromada simples', 'Cromada monocomando', 'Sem torneiras'] },
      { key: 'tipoChuveiro', label: 'Tipo de chuveiro', type: 'select', options: ['Chuveiro elétrico', 'Ducha a gás', 'Apenas ponto'] },
    ],
  },
  {
    id: 'estrutural',
    title: 'Estrutural',
    icon: 'M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z',
    fields: [
      { key: 'tipoFundacao', label: 'Tipo de fundação', type: 'select', options: ['Sapata', 'Radier', 'Estaca', 'Não se aplica'] },
      { key: 'fckConcreto', label: 'Fck do concreto', type: 'select', options: ['20 MPa', '25 MPa', '30 MPa'] },
      { key: 'tipoLaje', label: 'Tipo de laje', type: 'select', options: ['Maciça', 'Nervurada', 'Pré-moldada'] },
    ],
  },
];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function Specifications() {
  const navigate = useNavigate();
  const { uploadedFile, setAnalysisData } = useAnalysis();
  const [specs, setSpecs] = useState(DEFAULT_SPECS);
  const [openSections, setOpenSections] = useState(['geral']);
  const [exceptions, setExceptions] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!uploadedFile) {
      navigate('/upload');
    }
  }, [uploadedFile, navigate]);

  if (!uploadedFile) return null;

  const toggleSection = (id) => {
    setOpenSections((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const updateSpec = (key, value) => {
    setSpecs((prev) => ({ ...prev, [key]: value }));
  };

  const addException = () => {
    setExceptions((prev) => [...prev, { ambiente: '', especificacao: '' }]);
  };

  const updateException = (index, field, value) => {
    setExceptions((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeException = (index) => {
    setExceptions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAnalyze = async () => {
    setIsProcessing(true);
    setProgress(0);
    setError(null);

    LogService.log(LOG_ACTIONS.ANALYSIS_START, {
      details: { fileName: uploadedFile.name },
    });

    try {
      setProgress(10);
      const base64 = await fileToBase64(uploadedFile);
      setProgress(20);

      const validExceptions = exceptions.filter((e) => e.ambiente && e.especificacao);

      setProgress(30);
      const response = await fetch('/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          file: base64,
          fileName: uploadedFile.name,
          mimeType: uploadedFile.type,
          specifications: {
            ...specs,
            excecoes: validExceptions,
          },
        }),
      });

      setProgress(70);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erro ${response.status}: Falha ao processar.`);
      }

      const data = await response.json();
      setProgress(90);

      setAnalysisData({
        ...data,
        fileName: uploadedFile.name,
        specifications: specs,
      });

      LogService.log(LOG_ACTIONS.ANALYSIS_END, {
        details: {
          fileName: uploadedFile.name,
          itemCount: data.quantitativos?.length || 0,
          areaTotal: data.resumo?.area_total,
          tipoProject: data.resumo?.tipo_projeto,
        },
      });

      setProgress(100);
      setTimeout(() => navigate('/resultados'), 500);
    } catch (err) {
      LogService.log(LOG_ACTIONS.ANALYSIS_ERROR, {
        status: 'error',
        errorMessage: err.message,
        details: { fileName: uploadedFile.name },
      });
      setIsProcessing(false);
      setProgress(0);
      setError(err.message || 'Erro ao processar. Tente novamente.');
    }
  };

  const renderField = (field) => {
    const value = specs[field.key];

    if (field.type === 'checkbox') {
      return (
        <label key={field.key} className="flex items-center gap-3 py-2 cursor-pointer">
          <div
            onClick={() => updateSpec(field.key, !value)}
            className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer
              ${value ? 'bg-orca-600 border-orca-600' : 'border-gray-300 bg-white'}`}
          >
            {value && (
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <span className="text-gray-700 font-medium">{field.label}</span>
        </label>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.key} className="space-y-1.5">
          <label className="text-sm font-medium text-gray-600">{field.label}</label>
          <select
            value={value}
            onChange={(e) => updateSpec(field.key, e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent transition-all cursor-pointer"
          >
            {field.options.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    }

    if (field.type === 'number') {
      return (
        <div key={field.key} className="space-y-1.5">
          <label className="text-sm font-medium text-gray-600">{field.label}</label>
          <div className="relative">
            <input
              type="number"
              value={value}
              onChange={(e) => updateSpec(field.key, parseFloat(e.target.value) || 0)}
              step={field.step || 1}
              min={0}
              className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-200 bg-white text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent transition-all"
            />
            {field.suffix && (
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">
                {field.suffix}
              </span>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  if (isProcessing) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-8 md:py-16">
          <div className="bg-white rounded-3xl border border-gray-200 p-12 md:p-16 flex flex-col items-center gap-8">
            <div className="relative">
              <div className="w-24 h-24 rounded-full border-4 border-gray-200" />
              <div className="absolute top-0 left-0 w-24 h-24 rounded-full border-4 border-orca-600 border-t-transparent animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-bold text-orca-800">{progress}%</span>
              </div>
            </div>
            <div className="w-full max-w-md">
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-orca-600 h-3 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
            <div className="text-center">
              <p className="text-xl font-semibold text-gray-800">Analisando a planta...</p>
              <p className="text-gray-500 mt-2">
                {progress < 20 && 'Preparando arquivo...'}
                {progress >= 20 && progress < 30 && 'Convertendo para envio...'}
                {progress >= 30 && progress < 70 && 'Claude AI analisando a planta...'}
                {progress >= 70 && progress < 90 && 'Processando resultados...'}
                {progress >= 90 && 'Finalizando...'}
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-3xl mx-auto px-4 py-6 md:py-10">
        {/* Título */}
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">
          Especificações da Obra
        </h1>
        <p className="text-gray-500 mb-6">
          Configure os detalhes para um orçamento mais preciso
        </p>

        {/* Mini card do arquivo */}
        <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200 mb-6">
          <div className="w-10 h-10 bg-orca-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-orca-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-800 truncate">{uploadedFile.name}</p>
            <p className="text-xs text-gray-400">{(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB</p>
          </div>
          <button
            onClick={() => navigate('/upload')}
            className="text-xs text-gray-400 hover:text-orca-600 transition-colors cursor-pointer"
          >
            Trocar
          </button>
        </div>

        {/* Erro */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
            <p className="font-semibold">Erro no processamento</p>
            <p className="text-sm mt-1">{error}</p>
            <button
              onClick={() => setError(null)}
              className="mt-3 text-sm underline cursor-pointer"
            >
              Fechar
            </button>
          </div>
        )}

        {/* Accordion Sections */}
        <div className="space-y-3">
          {SECTIONS.map((section) => {
            const isOpen = openSections.includes(section.id);
            return (
              <div key={section.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center gap-3 p-4 md:p-5 text-left cursor-pointer hover:bg-gray-50/50 transition-colors"
                >
                  <div className="w-9 h-9 bg-orca-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-orca-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={section.icon} />
                    </svg>
                  </div>
                  <span className="flex-1 font-semibold text-gray-800">{section.title}</span>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isOpen && (
                  <div className="px-4 pb-5 md:px-5 border-t border-gray-100">
                    <div className="grid gap-4 mt-4 sm:grid-cols-2">
                      {section.fields.map(renderField)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Seção Exceções */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button
              onClick={() => toggleSection('excecoes')}
              className="w-full flex items-center gap-3 p-4 md:p-5 text-left cursor-pointer hover:bg-gray-50/50 transition-colors"
            >
              <div className="w-9 h-9 bg-orca-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-orca-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <span className="flex-1 font-semibold text-gray-800">Exceções</span>
              <span className="text-xs text-gray-400 mr-2">opcional</span>
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${openSections.includes('excecoes') ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {openSections.includes('excecoes') && (
              <div className="px-4 pb-5 md:px-5 border-t border-gray-100">
                <p className="text-sm text-gray-400 mt-3 mb-4">
                  Adicione exceções para ambientes com especificações diferentes do padrão
                </p>

                {exceptions.map((exc, index) => (
                  <div key={index} className="flex gap-3 mb-3 items-start">
                    <div className="flex-1 space-y-2 sm:space-y-0 sm:flex sm:gap-3">
                      <input
                        type="text"
                        placeholder="Ambiente (ex: Quartos)"
                        value={exc.ambiente}
                        onChange={(e) => updateException(index, 'ambiente', e.target.value)}
                        className="w-full sm:w-1/3 px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        placeholder="Especificação (ex: Piso laminado ao invés de porcelanato)"
                        value={exc.especificacao}
                        onChange={(e) => updateException(index, 'especificacao', e.target.value)}
                        className="w-full sm:flex-1 px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-orca-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      onClick={() => removeException(index)}
                      className="mt-2.5 sm:mt-0 p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer flex-shrink-0"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}

                <button
                  onClick={addException}
                  className="mt-2 flex items-center gap-2 text-sm font-medium text-orca-700 hover:text-orca-800 cursor-pointer py-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Adicionar exceção
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Botão Analisar */}
        <button
          onClick={handleAnalyze}
          className="mt-8 mb-12 w-full bg-orca-800 text-white text-xl font-bold py-5 rounded-2xl hover:bg-orca-700 active:bg-orca-900 transition-all duration-200 shadow-lg shadow-orca-800/20 cursor-pointer"
        >
          Analisar Planta
        </button>
      </main>
    </div>
  );
}

export default Specifications;
